// Phase 4a promo-codes — customer-facing apply/remove hooks for the
// in-progress cart's discount line. Server is the source of truth:
// every successful apply/remove triggers a cart-quote refetch so the
// `<PriceBreakdown>` shows the recomputed total + discount line in
// place. Error codes map to the FR-19 messages already rendered
// server-side; the UI just surfaces `message` verbatim.

import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { fetchJson, ApiError } from "@/lib/api";

const PROMO_URL = "/api/v1/cart/promo";

export interface PromoSummary {
  id: number;
  code: string;
  discount_type: "percent" | "fixed";
  discount_value: number;
}

export interface PromoApplyOk {
  ok: true;
  promo_code: PromoSummary;
}

export interface PromoFailure {
  ok: false;
  error_code: string;
  message: string;
}

// `usePromoApply` returns the server's success body on success and
// throws an `ApiError` (with the failure body intact) on rejection,
// matching the rest of the customer-side mutations.
export function usePromoApply(): UseMutationResult<PromoApplyOk, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) =>
      fetchJson<PromoApplyOk>(PROMO_URL, { method: "POST", body: { code } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: ["cart-quote"] });
    }
  });
}

export function usePromoRemove(): UseMutationResult<{ ok: true }, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => fetchJson<{ ok: true }>(PROMO_URL, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cart"] });
      qc.invalidateQueries({ queryKey: ["cart-quote"] });
    }
  });
}

// Helper for the UI: extract the human-readable message from an
// ApiError thrown by `usePromoApply`. Falls back to a generic string
// if the server returned an unexpected shape.
export function promoErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as PromoFailure | undefined;
    return body?.message ?? "Could not apply this code.";
  }
  return "Could not apply this code.";
}
